import React, { JSX } from 'react';
import classNames from 'classnames';

// components
import { AtText } from '@/components/atoms/at-text/at-text';
import { AtHeader } from '@/components/atoms/at-header/at-header';
import { AtNavLink } from '@/components/atoms/at-nav-link/at-nav-link';
import { AtDivider } from '@/components/atoms/at-divider/at-divider';

// types
import {
  EClickableSize,
  EClickableTarget,
} from '@/components/atoms/at-clickable/at-clickable.types';
import {
  EDividerColors,
  EDividerVariants,
  EDividerDirectionalVariants,
} from '@/components/atoms/at-divider/at-divider.types';
import { ENavLinkVariants } from '@/components/atoms/at-nav-link/at-nav-link.types';
import {
  IOrFooterProps,
  ILinksColumn,
  ILink,
  IActionColumnProps,
} from './or-footer.types';

import { FooterActions } from './or-footer-actions.client';

export const OrFooter = ({
  actionLinks,
  legalLinks,
  mainLinks,
}: IOrFooterProps): JSX.Element => {
  const { actionButtons, copyright, socialLinks } = actionLinks;

  return (
    <div data-theme="light">
      <footer className="pt-md px-5 xl:px-[3.25rem]">
        <div className="extendContainer mx-auto !w-auto max-w-[112.5rem] overflow-visible rounded-xl bg-skin-secondary !px-9 py-15 pt-20 text-black lg:mb-0 lg:pb-[3.375rem] lg:pt-36">
          <div className="mx-auto flex flex-col lg:flex-row lg:px-7">
            <ActionColumn
              actionButtons={actionButtons}
              socialLinks={socialLinks}
              className="block lg:hidden"
            />

            <nav
              aria-label="Footer"
              className="flex-1"
              data-testid="footer-bottom-content"
            >
              <ul
                className={classNames(
                  'grid auto-cols-auto grid-cols-1 gap-14',
                  'lg:grid-cols-2 lg:gap-8',
                  'xl:grid-cols-4 xl:gap-16',
                )}
              >
                {mainLinks.map((linkGroup, i) => (
                  <li
                    key={`footer_link_${i}`}
                    aria-setsize={mainLinks.length}
                    aria-posinset={i + 1}
                    className={classNames(
                      'col-span-1',
                      i === 1
                        ? 'lg:row-start-2 lg:-mt-4 xl:mt-0'
                        : 'xl:row-auto',
                      i === 3 ? 'lg:row-start-2' : 'xl:row-auto',
                    )}
                  >
                    <span className="sr-only">
                      Link group {i + 1} of {mainLinks.length}
                    </span>
                    <LinksColumn {...linkGroup} />
                  </li>
                ))}
              </ul>
            </nav>

            <ActionColumn
              actionButtons={actionButtons}
              socialLinks={socialLinks}
              className="hidden lg:block"
            />
          </div>

          <LegalLinks links={legalLinks} />
        </div>
      </footer>

      <AtText className="mb-[4.75rem] text-center !text-sm !leading-[3.125rem] lg:mb-0">
        {copyright}
      </AtText>
    </div>
  );
};

// =================
// Subcomponents
// =================

const LinksColumn = ({ header, links }: ILinksColumn) => (
  <>
    {!header && <h2 className="sr-only">Additional Links</h2>}
    {header && (
      <AtHeader
        element="h2"
        variant="h6"
        className="mb-7 text-skin-tertiary md:mb-12"
      >
        {header}
      </AtHeader>
    )}
    <ul className={`flex flex-col gap-4 ${!header ? 'xl:mt-12' : 'mt:0'}`}>
      {links.map((link, i) => (
        <li key={`footer-main-li-${i}`} className="text-skin-primary">
          <AtNavLink
            label={link.label}
            href={link.href}
            target={link?.target || EClickableTarget.SELF}
            variant={ENavLinkVariants.MENU}
            size={EClickableSize.LG}
            interactLocation="footer"
          />
        </li>
      ))}
    </ul>
  </>
);

const LegalLinks = ({ links }: { links: ILink[] }) => (
  <nav aria-labelledby="footer-legal-links" className="lg:px-7">
    <h2 id="footer-legal-links" className="sr-only">
      Legal Links
    </h2>
    <ul className="mt-20 flex flex-col gap-7 uppercase lg:flex-row lg:gap-6 xl:gap-12">
      {links.map((link, i) => (
        <li key={`footer-legal-link-${i}`}>
          <AtNavLink
            {...link}
            size={EClickableSize.SM}
            variant={ENavLinkVariants.INLINE}
            target={link?.target || EClickableTarget.SELF}
            interactLocation="footer"
            className="font-semibold text-skin-primary lg:text-13 xl:text-base"
          />
        </li>
      ))}
    </ul>
  </nav>
);

const ActionColumn = ({
  actionButtons,
  socialLinks,
  className,
}: IActionColumnProps & { className?: string }) => (
  <section aria-labelledby="footer-actions" className={className}>
    <h2 id="footer-actions" className="sr-only">
      Stay Connected & Call to Actions
    </h2>
    <div
      data-testid="footer-top-content"
      className="flex flex-col-reverse gap-12 lg:flex-row"
    >
      <AtDivider
        color={EDividerColors.WHITE}
        intent={EDividerVariants.THICK}
        direction={EDividerDirectionalVariants.HORIZONTAL}
        customClasses="h-0.5 w-full mb-60 lg:hidden xl:block xl:mb-0 xl:ml-60 xl:h-auto xl:w-0.5"
      />
      <FooterActions actionButtons={actionButtons} socialLinks={socialLinks} />
    </div>
  </section>
);

OrFooter.ActionColumn = ActionColumn;
OrFooter.LinksColumn = LinksColumn;
OrFooter.LegalLinks = LegalLinks;
